import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { depthValue, depthNoun } from '../lib/depth';
import { TypewriterText } from '../hooks/useTypewriter';

const API = '/api';

const VISIBILITY = [
  ['private', 'only you. it lives in your workspace until you publish it.'],
  ['unlisted', 'anyone with the link; never listed or searchable.'],
  ['public', 'published: listed, searchable, on your profile.'],
];

export default function Upload() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Optional sidecar .meta.json (v12.3.17+)
  const [metaFile, setMetaFile] = useState(null);
  const [metaFileName, setMetaFileName] = useState('');
  const [metaProfile, setMetaProfile] = useState(null);  // parsed profile block
  const [draggingMeta, setDraggingMeta] = useState(false);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [corpusTags, setCorpusTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [visibility, setVisibility] = useState('private');
  const [licence, setLicence] = useState('');
  const [licences, setLicences] = useState([]);
  useEffect(() => { fetch(`${API}/licences`).then(r => r.json()).then(setLicences).catch(() => {}); }, []);
  
  const [parsedData, setParsedData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [uploadedCheckpoint, setUploadedCheckpoint] = useState(null);
  const [shareCopied, setShareCopied] = useState(false);

  if (!user) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-6 py-16 page-wrapper" data-testid="upload-login-prompt">
        <div className="max-w-md w-full space-y-8 animate-scale-in">
          <div className="space-y-3">
            <p className="text-[#555555] lowercase text-sm">upload</p>
            <h1 className="text-3xl lg:text-4xl font-bold text-white lowercase leading-tight">
              share what you've trained.
            </h1>
            <p className="text-[#aaaaaa] lowercase leading-relaxed text-sm">
              upload a soma model: a single .pt file. others can download it, train it further, or talk to it. its history comes with it.
            </p>
          </div>

          <ul className="space-y-2 text-sm text-[#888888] lowercase">
            <li className="flex items-baseline gap-2">
              <span className="text-[#444444] font-mono text-xs">·</span>
              <span>sign in once with google. no email, no password, no profile setup beyond picking a username.</span>
            </li>
            <li className="flex items-baseline gap-2">
              <span className="text-[#444444] font-mono text-xs">·</span>
              <span>uploads go direct to storage — files up to 16 gb supported.</span>
            </li>
            <li className="flex items-baseline gap-2">
              <span className="text-[#444444] font-mono text-xs">·</span>
              <span>you stay the only person who can edit or delete your checkpoints.</span>
            </li>
          </ul>

          <div className="space-y-3">
            <button
              onClick={login}
              className="w-full border border-white px-6 py-3 text-white lowercase hover:bg-white hover:text-black transition-all duration-300 rounded-lg glow-hover"
              data-testid="login-to-upload-btn"
            >
              continue with google
            </button>
            <div className="flex items-center justify-between text-xs">
              <button
                onClick={() => navigate('/checkpoints')}
                className="text-[#666666] lowercase hover:text-white transition-colors underline decoration-[#333333] underline-offset-4 hover:decoration-white"
                data-testid="browse-checkpoints-link"
              >
                browse what's already up →
              </button>
              <button
                onClick={() => navigate('/specs')}
                className="text-[#666666] lowercase hover:text-white transition-colors underline decoration-[#333333] underline-offset-4 hover:decoration-white"
                data-testid="train-first-link"
              >
                haven't trained yet?
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    handleFile(file);
  };

  const handleFile = (file) => {
    if (!file) return;
    if (!file.name.endsWith('.pt')) {
      setError('only .pt files are accepted');
      return;
    }
    if (file.size > 16 * 1024 * 1024 * 1024) {
      setError('file exceeds the 16GB upload limit');
      return;
    }
    setSelectedFile(file);
    setFileName(file.name);
    setError(null);
  };

  // ---- optional sidecar .meta.json ----
  const handleMetaFile = (file) => {
    if (!file) return;
    if (!file.name.endsWith('.meta.json') && !file.name.endsWith('.json')) {
      setError('sidecar must be a .meta.json (or .json) file');
      return;
    }
    if (file.size > 1024 * 1024) {
      setError('sidecar .meta.json is larger than the 1 mb cap');
      return;
    }
    // Parse client-side so we can prefill the profile fields immediately.
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result);
        const src = parsed && typeof parsed === 'object' && parsed.profile ? parsed.profile : parsed;
        const profile = {};
        for (const k of ['name', 'tagline', 'art', 'about']) {
          if (src && typeof src[k] === 'string' && src[k].trim()) profile[k] = src[k];
        }
        setMetaFile(file);
        setMetaFileName(file.name);
        setMetaProfile(Object.keys(profile).length ? profile : null);
        setError(null);
      } catch {
        setError('sidecar .meta.json is not valid json');
      }
    };
    reader.onerror = () => setError('could not read sidecar file');
    reader.readAsText(file);
  };

  const handleMetaDragOver = (e) => { e.preventDefault(); setDraggingMeta(true); };
  const handleMetaDragLeave = () => setDraggingMeta(false);
  const handleMetaDrop = (e) => {
    e.preventDefault();
    setDraggingMeta(false);
    handleMetaFile(e.dataTransfer.files[0]);
  };
  const handleMetaSelect = (e) => handleMetaFile(e.target.files[0]);
  const clearMetaFile = () => {
    setMetaFile(null);
    setMetaFileName('');
    setMetaProfile(null);
  };

  const getAuthHeaders = () => {
    const headers = {};
    const token = localStorage.getItem('logos_session_token');
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      // 1. Get presigned URL from backend
      setUploadProgress(2);
      const presignRes = await fetch(`${API}/checkpoints/upload/presign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ filename: selectedFile.name, file_size: selectedFile.size })
      });
      if (!presignRes.ok) {
        const err = await presignRes.json().catch(() => ({}));
        throw new Error(err.detail || 'failed to initialize upload');
      }
      const { upload_id, presigned_url, r2_key } = await presignRes.json();

      // 2. Upload file directly to R2 (bypasses all proxies)
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            setUploadProgress(5 + Math.round((e.loaded / e.total) * 80)); // 5-85%
          }
        });
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`upload to storage failed (${xhr.status}). please try again.`));
        });
        xhr.addEventListener('error', () => reject(new Error('network error during upload. please check your connection and try again.')));
        xhr.addEventListener('timeout', () => reject(new Error('upload timed out. please try again with a stable connection.')));
        xhr.open('PUT', presigned_url);
        xhr.setRequestHeader('Content-Type', 'application/octet-stream');
        xhr.timeout = 0; // no timeout for large files
        xhr.send(selectedFile);
      });

      // 2b. If a sidecar .meta.json was provided, presign + PUT it too.
      let meta_r2_key_uploaded = null;
      if (metaFile) {
        setUploadProgress(86);
        const metaPresignRes = await fetch(`${API}/checkpoints/upload/presign_meta`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          credentials: 'include',
          body: JSON.stringify({ r2_key })
        });
        if (!metaPresignRes.ok) {
          const err = await metaPresignRes.json().catch(() => ({}));
          throw new Error(err.detail || 'failed to initialize sidecar upload');
        }
        const metaPresign = await metaPresignRes.json();
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) resolve();
            else reject(new Error(`sidecar upload failed (${xhr.status})`));
          });
          xhr.addEventListener('error', () => reject(new Error('sidecar upload network error')));
          xhr.open('PUT', metaPresign.presigned_url);
          xhr.setRequestHeader('Content-Type', 'application/json');
          xhr.send(metaFile);
        });
        meta_r2_key_uploaded = metaPresign.meta_r2_key;
      }

      // 3. Tell backend to confirm and parse
      setUploadProgress(88);
      const confirmRes = await fetch(`${API}/checkpoints/upload/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ upload_id, r2_key, filename: selectedFile.name, meta_r2_key: meta_r2_key_uploaded })
      });
      if (!confirmRes.ok) {
        const err = await confirmRes.json().catch(() => ({}));
        throw new Error(err.detail || 'upload processing failed');
      }
      setUploadProgress(100);

      const data = await confirmRes.json();
      const parsed = data.parsed;
      const serverProfile = data.profile || null;
      // Prefer the server-cleaned profile; fall back to what we parsed client-side.
      const finalProfile = serverProfile || metaProfile || null;
      setMetaProfile(finalProfile);
      setParsedData({
        ...parsed,
        file_size_bytes: data.file_size_bytes,
        r2_key: data.r2_key,
        meta_r2_key: data.meta_r2_key || null,
      });
      // Prefer profile.name / profile.tagline as name-and-description hints.
      const preferName = (finalProfile && finalProfile.name) || parsed.name || fileName.replace('.pt', '');
      setName(preferName);
      if (parsed.corpus_tags && parsed.corpus_tags.length > 0) setCorpusTags(parsed.corpus_tags);
      const preferDesc = (finalProfile && (finalProfile.tagline || finalProfile.about)) || parsed.description;
      if (preferDesc) setDescription(preferDesc);
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const addTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !corpusTags.includes(tag)) {
      setCorpusTags([...corpusTags, tag]);
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setCorpusTags(corpusTags.filter(t => t !== tag));
  };

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  const handleSubmit = async () => {
    if (!name.trim()) { setError('name is required'); return; }
    if (corpusTags.length === 0) { setError('at least one corpus tag is required'); return; }

    setSubmitting(true);
    setError(null);
    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = localStorage.getItem('logos_session_token');
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const response = await fetch(`${API}/checkpoints`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          name: name.trim(), description: description.trim() || null, visibility,
          licence: visibility === 'public' ? licence : null,
          corpus_tags: corpusTags, n_bands: parsedData.n_bands,
          hidden_dim: parsedData.hidden_dim, base: parsedData.base,
          direct_readout: parsedData.direct_readout, bytes_seen: parsedData.bytes_seen,
          soma_version: parsedData.soma_version, checkpoint_history: parsedData.checkpoint_history,
          checkpoint_id: parsedData.checkpoint_id,
          parameters: parsedData.parameters ?? null,
          config: parsedData.config ?? null,
          file_size_bytes: parsedData.file_size_bytes || 0,
          r2_key: parsedData.r2_key,
          // Sidecar .meta.json — optional, uploaded beside the .pt.
          meta_r2_key: parsedData.meta_r2_key || null,
          profile: metaProfile,
          // v12 fields (null on older checkpoints, server stores as-is)
          n_layers: parsedData.n_layers ?? 1,
          n_stacks: parsedData.n_stacks ?? parsedData.n_layers ?? 1,
          architecture: parsedData.architecture ?? null,
          species: parsedData.species ?? null,
          auto_mode: parsedData.auto_mode ?? null,
          lr_auto: parsedData.lr_auto ?? null,
          lr_base: parsedData.lr_base ?? null,
          max_change_auto: parsedData.max_change_auto ?? null,
          max_change_base: parsedData.max_change_base ?? null,
          decimation_auto: parsedData.decimation_auto ?? null,
          decimation_range: parsedData.decimation_range ?? null,
          decimation_band: parsedData.decimation_band ?? null,
          batch_size: parsedData.batch_size ?? null,
          scale_gate: parsedData.scale_gate ?? null,
          clock: parsedData.clock ?? null,
          weight_decay: parsedData.weight_decay ?? null,
          // v12.3 token-native metadata
          tokens_seen: parsedData.tokens_seen ?? null,
          vocab_size: parsedData.vocab_size ?? null,
          token_dim: parsedData.token_dim ?? null,
          tokenizer_name: parsedData.tokenizer_name ?? null,
          tokenizer_sha256: parsedData.tokenizer_sha256 ?? null,
          runtime_version: parsedData.runtime_version ?? null,
          revision_id: parsedData.revision_id ?? null,
        })
      });
      if (response.ok) {
        const checkpoint = await response.json();
        setUploadedCheckpoint(checkpoint);
        setStep(3);
      } else {
        const data = await response.json();
        throw new Error(data.detail || 'Upload failed');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const copyShareLink = async () => {
    if (!uploadedCheckpoint) return;
    const url = `${window.location.origin}/api/share/${uploadedCheckpoint.checkpoint_id}`;
    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch {}
  };

  return (
    <div className="min-h-screen bg-black py-12 px-6 lg:px-8 page-wrapper" data-testid="upload-page">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-4xl lg:text-5xl font-bold text-white lowercase mb-12 animate-slide-up">
          <TypewriterText text="upload checkpoint" speed={55} startDelay={150} />
        </h1>

        {error && (
          <div className="border border-[#383f38] rounded-lg p-4 mb-8 text-white lowercase animate-fade-in" data-testid="upload-error">
            {error}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-8 animate-fade-in">
            {/* Drop Zone */}
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`drop-zone border-2 border-dashed p-16 lg:p-24 flex flex-col items-center justify-center text-center cursor-pointer rounded-2xl ${
                dragging ? 'border-white text-white bg-white/5' : 'border-[#272e28] text-[#888888] hover:border-[#4a514a]'
              }`}
              onClick={() => document.getElementById('file-input').click()}
              data-testid="drop-zone"
            >
              <input id="file-input" type="file" accept=".pt" onChange={handleFileSelect} className="hidden" />
              <p className="text-xl lowercase mb-2">
                {fileName || 'drop your .pt checkpoint here, or click to browse'}
              </p>
              <p className="text-sm lowercase">max file size: 16gb</p>
            </div>

            {/* Optional sidecar .meta.json (v12.3.17+). */}
            <div
              onDragOver={handleMetaDragOver}
              onDragLeave={handleMetaDragLeave}
              onDrop={handleMetaDrop}
              className={`border border-dashed rounded-xl p-6 flex items-center justify-between gap-4 cursor-pointer transition-colors ${
                draggingMeta ? 'border-white text-white bg-white/5' : 'border-[#1e251f] text-[#666666] hover:border-[#4a514a]'
              }`}
              onClick={() => document.getElementById('meta-file-input').click()}
              data-testid="meta-drop-zone"
            >
              <input id="meta-file-input" type="file" accept=".json,.meta.json,application/json" onChange={handleMetaSelect} className="hidden" />
              <div className="min-w-0">
                <p className="lowercase text-sm text-white truncate">
                  {metaFileName || 'optional: drop your .meta.json sidecar'}
                </p>
                <p className="text-xs lowercase text-[#555555] mt-1">
                  {metaProfile
                    ? `profile parsed · ${Object.keys(metaProfile).join(', ')}`
                    : 'name · tagline · art · about — up to 1 mb. skip if you don\'t have one.'}
                </p>
              </div>
              {metaFileName && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); clearMetaFile(); }}
                  className="text-[#666666] hover:text-white text-xs lowercase underline decoration-[#333333] underline-offset-4"
                  data-testid="meta-clear-btn"
                >
                  remove
                </button>
              )}
            </div>

            {uploading && (
              <div className="space-y-2">
                <div className="progress-bar">
                  <div className="progress-bar-fill" style={{ width: `${uploadProgress}%` }} />
                </div>
                <p className="text-[#888888] text-sm lowercase text-center">
                  {uploadProgress < 90 ? `uploading... ${uploadProgress}%` :
                   uploadProgress < 100 ? 'parsing checkpoint and uploading to storage...' :
                   'done!'}
                </p>
              </div>
            )}

            {fileName && !uploading && (
              <button
                onClick={handleUpload}
                className="w-full border border-[#383f38] px-8 py-4 text-white lowercase hover:bg-white hover:text-black transition-all duration-300 rounded-lg glow-hover"
                data-testid="upload-parse-btn"
              >
                upload and parse
              </button>
            )}
          </div>
        )}

        {step === 2 && parsedData && (
          <div className="space-y-8 animate-fade-in">
            {/* Parsed Data */}
            <div className="border border-[#272e28] rounded-xl p-6 space-y-4 gradient-card">
              <h2 className="text-xl font-bold text-white lowercase mb-1">parsed from file</h2>
              <p className="text-[#666666] lowercase text-xs mb-4">read directly from your .pt — these aren't editable.</p>

              {/* Shape — the headline */}
              <div className="border border-[#1e251f] rounded-lg p-4 bg-[#0e120f]">
                <label className="block text-[#666666] lowercase text-xs mb-2 font-mono">model shape</label>
                <p className="text-white font-mono text-base lowercase break-words">
                  {parsedData.n_bands} bands · h={parsedData.hidden_dim > 0 ? parsedData.hidden_dim : 'linear'} · {depthValue(parsedData)} {depthNoun(parsedData)}{depthValue(parsedData) !== 1 ? 's' : ''} · {parsedData.soma_version || 'unknown'}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <div>
                  <label className="text-[#666666] lowercase text-xs font-mono">checkpoint_id</label>
                  <p className="text-white font-mono">{parsedData.checkpoint_id.slice(0, 12)}…</p>
                </div>
                <div>
                  <label className="text-[#666666] lowercase text-xs font-mono">history length</label>
                  <p className="text-white">{parsedData.checkpoint_history.length}</p>
                </div>
                <div>
                  <label className="text-[#666666] lowercase text-xs font-mono">bytes_seen</label>
                  <p className="text-white font-mono">{parsedData.bytes_seen.toLocaleString()}</p>
                </div>
                <div>
                  <label className="text-[#666666] lowercase text-xs font-mono">base</label>
                  <p className="text-white font-mono">{Number(parsedData.base).toFixed(4)}</p>
                </div>
                {parsedData.batch_size != null && (
                  <div>
                    <label className="text-[#666666] lowercase text-xs font-mono">batch</label>
                    <p className="text-white font-mono">{parsedData.batch_size.toLocaleString()}</p>
                  </div>
                )}
                {parsedData.auto_mode && (
                  <div>
                    <label className="text-[#666666] lowercase text-xs font-mono">controller</label>
                    <p className="text-white font-mono lowercase">{parsedData.auto_mode}</p>
                  </div>
                )}
                {parsedData.lr_auto != null && (
                  <div>
                    <label className="text-[#666666] lowercase text-xs font-mono">lr</label>
                    <p className="text-white font-mono lowercase">
                      {parsedData.lr_auto ? `auto · base ${parsedData.lr_base ?? '?'}` : (parsedData.lr_base ?? 'fixed')}
                    </p>
                  </div>
                )}
                {parsedData.max_change_auto != null && (
                  <div>
                    <label className="text-[#666666] lowercase text-xs font-mono">max change</label>
                    <p className="text-white font-mono lowercase">
                      {parsedData.max_change_auto ? `auto · base ${parsedData.max_change_base ?? '?'}` : (parsedData.max_change_base ?? 'fixed')}
                    </p>
                  </div>
                )}
                {parsedData.decimation_auto != null && (
                  <div>
                    <label className="text-[#666666] lowercase text-xs font-mono">decimation</label>
                    <p className="text-white font-mono lowercase">
                      {parsedData.decimation_auto
                        ? `auto · gain ${parsedData.decimation_range ?? '?'}`
                        : `band ${parsedData.decimation_band ?? '?'}`}
                    </p>
                  </div>
                )}
                {parsedData.meta_r2_key && (
                  <div>
                    <label className="text-[#666666] lowercase text-xs font-mono">sidecar</label>
                    <p className="text-white font-mono lowercase">.meta.json attached</p>
                  </div>
                )}
                {parsedData.tokenizer_name && (
                  <div>
                    <label className="text-[#666666] lowercase text-xs font-mono">tokenizer</label>
                    <p className="text-white font-mono lowercase break-all">{parsedData.tokenizer_name}</p>
                  </div>
                )}
                {parsedData.vocab_size ? (
                  <div>
                    <label className="text-[#666666] lowercase text-xs font-mono">vocab</label>
                    <p className="text-white font-mono">
                      {parsedData.vocab_size.toLocaleString()}
                      {parsedData.token_dim ? ` · dim ${parsedData.token_dim}` : ''}
                    </p>
                  </div>
                ) : null}
                {parsedData.tokens_seen ? (
                  <div>
                    <label className="text-[#666666] lowercase text-xs font-mono">tokens_seen</label>
                    <p className="text-white font-mono">{parsedData.tokens_seen.toLocaleString()}</p>
                  </div>
                ) : null}
                {parsedData.direct_readout && (
                  <div>
                    <label className="text-[#666666] lowercase text-xs font-mono">direct readout</label>
                    <p className="text-white font-mono lowercase">on (legacy)</p>
                  </div>
                )}
              </div>
            </div>

            {/* Editable Fields */}
            <div className="space-y-6">
              <div>
                <label className="block text-[#888888] lowercase mb-2">name *</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                  className="w-full bg-[#0e120f] border border-[#272e28] text-white px-4 py-3 focus:border-[#4a514a] transition-all duration-300 lowercase rounded-lg"
                  placeholder="checkpoint name" data-testid="input-name" />
              </div>
              <div>
                <label className="block text-[#888888] lowercase mb-2">corpus tags * (min 1)</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {corpusTags.map(tag => (
                    <span key={tag} className="tag-chip flex items-center space-x-2">
                      <span>{tag}</span>
                      <button onClick={() => removeTag(tag)} className="text-[#888888] hover:text-white ml-1">×</button>
                    </span>
                  ))}
                </div>
                <div className="flex space-x-2">
                  <input type="text" value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleTagKeyDown}
                    className="flex-1 bg-[#0e120f] border border-[#272e28] text-white px-4 py-3 focus:border-[#4a514a] transition-all duration-300 lowercase rounded-lg"
                    placeholder="add tag" data-testid="input-tag" />
                  <button onClick={addTag}
                    className="border border-[#383f38] px-4 py-3 text-white lowercase hover:bg-white hover:text-black transition-all duration-300 rounded-lg"
                    data-testid="add-tag-btn">add</button>
                </div>
              </div>
              <div>
                <label className="block text-[#888888] lowercase mb-2">description (optional)</label>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4}
                  className="w-full bg-[#0e120f] border border-[#272e28] text-white px-4 py-3 focus:border-[#4a514a] transition-all duration-300 lowercase resize-none rounded-lg"
                  placeholder="describe your checkpoint" data-testid="input-description" />
              </div>
              <div>
                <label className="block text-[#888888] lowercase mb-2">who can see it</label>
                <div className="flex gap-2 flex-wrap" data-testid="upload-visibility">
                  {VISIBILITY.map(([v]) => (
                    <button key={v} type="button" onClick={() => setVisibility(v)}
                      className={`font-mono text-sm lowercase px-4 py-1.5 rounded-full border transition-all ${
                        visibility === v ? 'border-white text-white bg-white/5' : 'border-[#383f38] text-[#888888] hover:text-white'}`}
                      data-testid={`upload-visibility-${v}`}>{v}</button>
                  ))}
                </div>
                <p className="font-mono text-xs text-[#666666] lowercase mt-2">{VISIBILITY.find(([v]) => v === visibility)[1]}</p>
                {visibility === 'public' && (
                  <label className="flex items-center gap-3 font-mono text-xs text-[#888888] lowercase mt-3">
                    licence
                    <select value={licence} onChange={e => setLicence(e.target.value)} data-testid="upload-licence"
                      className="bg-transparent border border-[#383f38] rounded-lg px-2 py-1.5 text-white outline-none focus:border-white max-w-xs">
                      <option value="" className="bg-black">choose one</option>
                      {licences.map(l => <option key={l.id} value={l.id} className="bg-black">{l.label}</option>)}
                    </select>
                  </label>
                )}
              </div>
            </div>

            <button onClick={handleSubmit}
              disabled={submitting || !name.trim() || corpusTags.length === 0 || (visibility === 'public' && !licence)}
              className="w-full border border-[#383f38] px-8 py-4 text-white lowercase hover:bg-white hover:text-black transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg glow-hover"
              data-testid="submit-checkpoint-btn">
              {submitting ? 'submitting...' : visibility === 'public' ? 'upload and publish' : 'upload'}
            </button>
          </div>
        )}

        {step === 3 && uploadedCheckpoint && (
          <div className="space-y-8 animate-fade-in" data-testid="upload-success">
            <div className="border border-[#272e28] rounded-xl p-8 lg:p-10 gradient-card">
              <p className="text-[#555555] lowercase text-sm mb-2">uploaded.</p>
              <h2 className="text-3xl lg:text-4xl font-bold text-white lowercase mb-6 break-words">
                {uploadedCheckpoint.name}
              </h2>
              <p className="text-[#888888] lowercase text-sm mb-8">
                {uploadedCheckpoint.visibility === 'private'
                  ? "it's in your workspace, private. publish it from its page when you want it on the square."
                  : uploadedCheckpoint.visibility === 'unlisted'
                    ? "anyone with the link can see it; it isn't listed. copy the share link to send it."
                    : "it's live. copy the share link to post it anywhere — the dark card renders automatically."}
              </p>

              {uploadedCheckpoint.visibility !== 'private' && <div className="flex items-center gap-3 border border-[#202721] rounded-lg px-4 py-3 bg-[#0e120f] mb-6">
                <code className="text-[#aaaaaa] text-sm truncate flex-1">
                  {`${window.location.origin}/api/share/${uploadedCheckpoint.checkpoint_id}`}
                </code>
                <button onClick={copyShareLink}
                  className="border border-[#383f38] px-4 py-1.5 text-white lowercase text-sm hover:bg-white hover:text-black transition-all duration-300 rounded-full shrink-0"
                  data-testid="copy-share-link-btn">
                  {shareCopied ? 'copied' : 'copy'}
                </button>
              </div>}

              <div className="flex flex-col sm:flex-row gap-3">
                <button onClick={() => navigate(`/checkpoints/${uploadedCheckpoint.checkpoint_id}`)}
                  className="border border-[#383f38] px-6 py-3 text-white lowercase hover:bg-white hover:text-black transition-all duration-300 rounded-lg glow-hover"
                  data-testid="go-to-checkpoint-btn">
                  view checkpoint →
                </button>
                <button onClick={() => {
                    setStep(1); setSelectedFile(null); setFileName(''); setParsedData(null);
                    setName(''); setDescription(''); setCorpusTags([]); setTagInput(''); setVisibility('private');
                    setMetaFile(null); setMetaFileName(''); setMetaProfile(null);
                    setUploadedCheckpoint(null); setError(null);
                  }}
                  className="text-[#555555] lowercase hover:text-white transition-all duration-300 px-4 py-3"
                  data-testid="upload-another-btn">
                  upload another
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
